import Map from 'ol/Map';
import Source from 'ol/source/Source';
import OSM from 'ol/source/OSM';
import LayerRenderer from 'ol/renderer/Layer';
import OLLayer, { Options } from 'ol/layer/Layer';

import { Identifiable, MapCommand, MapDispatch } from 'controllers';
import Interpolator from 'utils/interpolator';

import { AuthManager, LayerManager } from 'models';

import { TileArcGISRest } from 'ol/source';

export interface Action {
    title: string;
}

export interface AbstractLayerProps {
    /**
     * The Layer Configuration to use
     */
    config: LayerManager.LayerConfig;
    visible?: boolean;
    opacity?: number;
    zIndex?: number;
    action?: Action;
    auth?: AuthManager.OAuth2Creds | null;
    filter?: string;
}

export type LayerProps = Omit<Partial<Options<Source>>, 'map'> & {
    map?: InstanceType<typeof Map>;
};

export type Event = 'update' | 'init';

export type EventCallback = (layer: AbstractLayer) => void | Promise<void>;

const OPACITY_ANIMATION_DURATION = 500; // ms

/**
 * An AbstractLayer acts as the bridge between OpenLayers Layers and
 * the 'MapDispatch' pattern used to integrate React to OpenLayers.
 *
 * It additionally implements many shared functionality that is used
 * thoughout the application such as Layer Lists and Controls that can
 * update layer settings, such as visiblity, opacity and order (zIndex).
 *
 * It also generically prepares to handle authentication (although this
 * tends to be implementation specific), filtering and animating its
 * opacity.
 */
export abstract class AbstractLayer<
    Layer extends OLLayer<Source, LayerRenderer<any>> = OLLayer<
        Source,
        LayerRenderer<any>
    >,
>
    implements MapCommand, Identifiable
{
    protected layer?: Layer;

    protected auth?: AuthManager.OAuth2Creds | null;

    protected map?: Map;

    protected dispatch?: MapDispatch;

    public action?: Action;

    public readonly id: LayerManager.Layer.Id;

    public readonly config: LayerManager.LayerConfig;

    protected abstract initializeLayer(props: LayerProps, map: Map): Layer;

    protected abstract applyAuthentication(): void;

    protected filter?: string;

    private initVisible: boolean;

    private initOpacity: number;

    private initZIndex: number;

    private opacityInterpolator: Interpolator;

    private callbacks: Record<Event, EventCallback[]> = {
        update: [],
        init: [],
    };

    get visible() {
        return this?.layer?.getVisible() ?? false;
    }

    set visible(value: boolean) {
        this?.layer?.setVisible(value);
        // When layer is enabled set the index to top index
        if (value && this?.layer?.getZIndex() == this.initZIndex && this.map) {
            const visibleLayers = this.map
                .getLayers()
                .getArray()
                .filter((l) => l.getVisible() == true);
            // Not sure why -2 is required otherwise it is showing wrong index
            const topLayerIndex: number = (visibleLayers?.length || 0) - 2;
            if (topLayerIndex > 0) {
                this?.layer?.setZIndex(topLayerIndex);
            }
        } else if (!value && this?.layer?.getZIndex() != this.initZIndex) {
            this?.layer?.setZIndex(this.initZIndex);
        }
        this.trigger('update');
    }

    get opacity() {
        return this?.layer?.getOpacity() ?? 0;
    }

    set opacity(value: number) {
        this?.layer?.setOpacity(value);
        this.opacityInterpolator.reset();
        this.trigger('update');
    }

    get zIndex() {
        return this?.layer?.getZIndex() ?? 0;
    }

    set zIndex(value: number) {
        this?.layer?.setZIndex(value);
        this.trigger('update');
    }

    defaultZIndex = (config: any) => {
        if (config.isBaseMap) return 0;
        //else if (config.type == 'CommentLayer') return 1;
        //else return 99999;
        return 99999;
    };

    constructor({
        config,
        visible,
        opacity,
        zIndex,
        action,
        auth,
        filter,
    }: AbstractLayerProps) {
        this.initVisible = visible ?? false;
        this.initOpacity = opacity ?? (+(config.isBaseMap ?? false) || 0.8);
        this.initZIndex = zIndex ?? this.defaultZIndex(config);
        this.action = action;
        this.config = config;
        this.auth = auth;
        this.id = config.id;
        this.filter = filter;

        this.opacityInterpolator = new Interpolator(
            OPACITY_ANIMATION_DURATION,
            (value) => {
                this?.layer?.setOpacity(value);
                this.trigger('update');
            },
        );

        if (this.auth) this.applyAuthentication();
    }

    identifiable: Identifiable['identifiable'] = () => {
        if (this.config.noPopup) return false;
        return true;
    };

    apply(map: Map, dispatch: MapDispatch) {
        this.map = map;
        this.dispatch = dispatch;

        this.layer = this.initializeLayer(
            {
                visible: this.initVisible,
                opacity: this.initOpacity,
                zIndex: this.initZIndex,
            },
            map,
        );

        this.layer.set('controller', this);

        if (this.layer) map.addLayer(this.layer);

        this.trigger('init');
        this.trigger('update');
    }

    updateAuthentication(auth?: AuthManager.OAuth2Creds | null) {
        this.auth = auth;
        this.applyAuthentication();
    }

    animateOpacity(value: number, onComplate?: () => void) {
        this.opacityInterpolator.reset();
        this.opacityInterpolator.start(this.opacity, value, onComplate);
    }

    getLayerIndex(layer: any) {
        // Prefer the controller's `isBaseMap` flag — this is authoritative
        // and handles every basemap source type, including LayerGroup-backed
        // Composite basemaps that have no `getSource()` method (calling
        // .getSource() on them throws and breaks the sort).
        const controller = layer.get?.('controller') as
            | AbstractLayer
            | undefined;
        if (controller?.config?.isBaseMap) {
            layer.setZIndex(0);
            return 0;
        }

        // Legacy fallback for layers that somehow lack a controller pointer.
        const source =
            typeof layer.getSource === 'function' ? layer.getSource() : null;
        if (source instanceof OSM || source instanceof TileArcGISRest) {
            layer.setZIndex(0);
            return 0;
        }
        return layer.getZIndex() ? layer.getZIndex() : 99999;
    }

    getOrderPosition() {
        if (!this.map || !this.layer) return null;
        let layers = this.map
            .getLayers()
            .getArray()
            .filter((l) => l.getVisible() == true);
        layers = layers.sort(
            (a, b) => this.getLayerIndex(a) - this.getLayerIndex(b),
        );
        return layers.indexOf(this.layer);
    }

    public getLayer() {
        return this.layer;
    }

    public onActionClick(_onCancel?: () => void) {
        console.log('Unimplemented Action');
    }

    public refresh() {
        this.layer?.getSource()?.refresh();
    }

    private moveLayer(diff: number) {
        if (this.map && this.layer) {
            let visibleLayers = this.map
                .getLayers()
                .getArray()
                .filter((l) => l.getVisible() == true);
            visibleLayers = visibleLayers.sort(
                (a, b) => this.getLayerIndex(a) - this.getLayerIndex(b),
            );
            const currentPosition = visibleLayers.indexOf(this.layer);
            const newPosition = currentPosition + diff;
            // console.log(
            //     `visibleLayers:${visibleLayers.length}. newPosition:${newPosition}`,
            // );
            if (newPosition <= 0 || newPosition >= visibleLayers.length - 1)
                return;
            const temp = visibleLayers[currentPosition + diff];
            visibleLayers[currentPosition + diff] =
                visibleLayers[currentPosition];
            visibleLayers[currentPosition] = temp;

            if (temp.get('controller')) {
                (temp.get('controller') as AbstractLayer).trigger('update');
            } else {
                console.log(temp);
            }

            visibleLayers.forEach((layer, idx) => {
                layer?.setZIndex(idx);
            });

            this.trigger('update');
        }
    }

    moveUp() {
        this.moveLayer(1);
    }

    moveDown() {
        this.moveLayer(-1);
    }

    protected trigger(event: Event) {
        this.callbacks[event].forEach((cb) => cb(this));
    }

    public on(event: Event, callback: (layer: AbstractLayer) => void) {
        this.callbacks[event].push(callback);
    }

    public un(event: Event, callback: (layer: AbstractLayer) => void) {
        this.callbacks[event] = this.callbacks[event].filter(
            (cb) => cb !== callback,
        );
    }

    public getFilter() {
        return this.filter;
    }

    public setFilter(filter: string) {
        this.filter = filter;
    }
}

export default AbstractLayer;
